import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { UserViewStore } from "../../store/UserViewStore";
import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { ShowReference } from "../../../common/controls";
import { Language } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { ShowFilesCompact } from "../../../common/controls";
import { IMenuAction } from "../../../../common/controls";
import { ShowBoolean } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { IdCardType } from "../../../../api/graphql";
import { Gender } from "../../../../api/graphql";

export type SiteUserViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  nameValue?: { displayId: string; id: string } | null;
  name: string;
  emailValue?: { displayId: string; id: string } | null;
  email: string;
  roleValue: { displayId: string; id: string } | null;
  languageOptions: { id: string; displayId: string }[];
  language: Language;
  avatarFiles: CrudRows;
  avatarViewAction?: IMenuAction[];
  activeValue?: { displayId: string; id: string } | null;
  active: boolean;
  cellphoneValue?: { displayId: string; id: string } | null;
  cellphone: string;
  twoFaseAuthValue?: { displayId: string; id: string } | null;
  twoFaseAuth: boolean;
  idCardTypeOptions: { id: string; displayId: string }[];
  idCardType: IdCardType | "";
  idCardValue?: { displayId: string; id: string } | null;
  idCard: string;
  genderOptions: { id: string; displayId: string }[];
  gender: Gender | "";
  userViewStore: UserViewStore;
};

export const SiteUserViewForm: React.FC<SiteUserViewFormProps> = observer(
  ({
    onPrint = () => {},
    onCancel = () => {},
    onEdit,
    showToolbox = true,
    name = "",
    email = "",
    roleValue,
    languageOptions = [],
    language = "" as Language,
    avatarFiles,
    avatarViewAction,
    active = false,
    cellphone = "",
    twoFaseAuth = false,
    idCardTypeOptions = [],
    idCardType = "" as IdCardType | "",
    idCard = "",
    genderOptions = [],
    gender = "" as Gender | "",
    userViewStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    const componentRef = useRef(null);
    const handlePrint = useReactToPrint({
      content: () => componentRef.current,
    });

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            editForm={onEdit}
            printForm={() => onPrint(handlePrint)}
            closeForm={() => onCancel()}
          />
        )}
        <Box ref={componentRef}>
          <GroupContainer
            id="main"
            title={langStore.groupSiteUserMainLabel}
            collapsible={true}
            expanded={userViewStore.getCardState("main")}
            toggleExpand={() => userViewStore.toggleCardState("main")}
          >
            <FormControl fullWidth>
              <ShowMultiLine value={name} label={langStore.UserNameLabel} />
              <ShowMultiLine value={email} label={langStore.UserEmailLabel} />
              <ShowReference
                label={langStore.UserRoleLabel}
                selectedValue={langStore.displayIdByLang(roleValue?.displayId)}
              />
              <ShowString
                value={
                  language
                    ? languageOptions.find(
                        (o: { id: string }) => o.id === language
                      )?.displayId || language
                    : ""
                }
                label={langStore.UserLanguageLabel}
              />
              <ShowFilesCompact
                rows={avatarFiles}
                showLabel={true}
                label={langStore.UserAvatarLabel}
                menuActions={avatarViewAction}
                columns={[
                  { name: "mimetype", title: langStore.fileTypeLabel },
                  { name: "name", title: langStore.fileNameLabel },
                  { name: "formattedSize", title: langStore.fileSizeLabel },
                ]}
              />
              <ShowBoolean value={active} label={langStore.UserActiveLabel} />
              <ShowMultiLine
                value={cellphone}
                label={langStore.UserCellphoneLabel}
              />
              <ShowBoolean
                value={twoFaseAuth}
                label={langStore.UserTwoFaseAuthLabel}
              />
            </FormControl>
          </GroupContainer>

          <GroupContainer
            id="details"
            title={langStore.groupSiteUserDetailsLabel}
            collapsible={true}
            expanded={userViewStore.getCardState("details")}
            toggleExpand={() => userViewStore.toggleCardState("details")}
          >
            <FormControl fullWidth>
              <ShowString
                value={
                  idCardType
                    ? idCardTypeOptions.find(
                        (o: { id: string }) => o.id === idCardType
                      )?.displayId || idCardType
                    : ""
                }
                label={langStore.UserIdCardTypeLabel}
              />
              <ShowMultiLine value={idCard} label={langStore.UserIdCardLabel} />
              <ShowString
                value={
                  gender
                    ? genderOptions.find((o: { id: string }) => o.id === gender)
                        ?.displayId || gender
                    : ""
                }
                label={langStore.UserGenderLabel}
              />
            </FormControl>
          </GroupContainer>
        </Box>
      </Grid>
    );
  }
);
